import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Accordion, AccordionTab } from 'primereact/accordion';

import { checkOverdueDatetime, formatDatetime } from 'utils/func';

import { TIMEZONE_VN, TOAST } from 'constant';
import {
  useGetRegisterResultReviewHistories,
  useGetRegisterResultReviews,
  useSendReviewFeedback,
} from 'utils/hooks/registerResult';
import { useGetProjectById } from 'utils/hooks/projects';
import { Button } from 'primereact/button';
import RegisterResultReviewHistory from 'features/Project/components/RegisterResultReviewHistory';
import { useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { TextareaInput } from 'components/FormControl';
import { useForm } from 'react-hook-form';
import { toast } from 'layout';
import { Tooltip } from 'primereact/tooltip';

export default function SubmitResultRequest() {
  // #region Data
  const { t } = useTranslation();
  const { projectId, registerResultId } = useParams();

  const [visible, setVisible] = useState(false);
  const [isFeedbackModalVisible, setIsFeedbackModalVisible] = useState(false);
  const [reviewId, setReviewId] = useState(null);

  const { data: projectData } = useGetProjectById(projectId, {});

  const { data, refetch } = useGetRegisterResultReviews(registerResultId, {});
  const { data: registerResultReviewHistories } = useGetRegisterResultReviewHistories(
    registerResultId,
    {}
  );

  const registerResultReviewHistoriesUsed = useMemo(() => {
    const result = [];
    let temp = [];
    registerResultReviewHistories?.forEach((item) => {
      if (item.action === 'reset') {
        result.push(temp);
        temp = [];
        temp.push(item);
      } else {
        temp.push(item);
      }
    });
    result.push(temp);
    return result;
  }, [registerResultReviewHistories]);

  const {
    control,
    getValues,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
  });

  const { mutateAsync: sendFeedback } = useSendReviewFeedback();

  // #region Render
  const formatHeader = (value, rowIndex) => {
    const requestType = t('project.viewHistory.status.registerResult');

    const deadline = value?.deadline;

    const date = new Date(deadline);

    // Trừ đi 1 giây
    date.setSeconds(date.getSeconds() - 1);

    let statusJsx = null;
    if (value?.feedback) {
      statusJsx = (
        <span className="font-medium text-green-500 text-base font-italic">
          {t('projectProposal.requestExplanation.ownerAddedExplanation')}
        </span>
      );
    } else if (!value?.editable) {
      statusJsx = <span />;
    } else if (value?.editable && checkOverdueDatetime(deadline)) {
      statusJsx = (
        <span className="font-medium text-red-500 text-base font-italic">
          {t('projectProposal.requestExplanation.overdueDeadline')}
        </span>
      );
    } else {
      statusJsx = (
        <span className="font-medium text-red-500 text-base font-italic">
          {t('project.requestExplanation.deadline')}:{' '}
          {deadline
            ? new Date(date).toLocaleString('en-GB', { timeZone: TIMEZONE_VN })
            : t('project.requestExplanation.noDeadline')}
        </span>
      );
    }

    return (
      <div className="flex flex-column text-lg font-bold gap-1">
        <span>
          {rowIndex + 1}. {requestType}
        </span>
        <div>{statusJsx} </div>
      </div>
    );
  };

  const formatComment = (value) => {
    if (!value?.comment) return null;

    return (
      <div>
        {value?.comment.map((comment) => (
          <div
            style={{ whiteSpace: 'pre-wrap' }}
            className="text-color-secondary mb-2"
            key={comment?.id}
          >
            -----------------------------------------------
            <span className="block mb-2">
              ({comment?.email} - {formatDatetime(comment?.createdAt, 'DD/MM/YYYY HH:mm:ss')})
            </span>
            {comment?.content}
          </div>
        ))}
      </div>
    );
  };

  const formatFeedback = (value) => {
    if (value?.feedback) {
      return (
        <div className="mb-2">
          <span className="font-semibold">{t('project.requestExplanation.feedback')}:</span>
          <p style={{ whiteSpace: 'pre-wrap' }}>
            {`--------------------------------------------------\n${value?.feedback}`}
          </p>
        </div>
      );
    }
    return null;
  };

  const formatActions = (value) => {
    const deadline = value?.deadline;
    const isRevise = deadline && !checkOverdueDatetime(deadline) && value?.editable;
    const isFeedback = deadline && !checkOverdueDatetime(deadline) && !value?.editable;

    return (
      <div>
        {isRevise && (
          <div className="flex justify-content-end gap-1">
            <div className="border-round-lg" style={{ backgroundColor: '#F59E0B' }}>
              <Link
                className="edit-project-proposal flex align-items-center justify-content-center font-bold text-white border-round m-2 h-2rem"
                to={`/project/${projectId}/register-result${
                  projectData?.liquidationCouncilStatus === 'qualified' ? '-liquidation' : ''
                }/edit/${registerResultId}?reviewId=${value?.id}`}
              >
                Chỉnh sửa phiếu đăng ký
              </Link>
            </div>
          </div>
        )}
        {isFeedback && (
          <div className="flex justify-content-end gap-1">
            <Button
              className="edit-project-proposal flex align-items-center justify-content-center font-bold text-white border-round"
              onClick={() => {
                setReviewId(value?.id);
                setIsFeedbackModalVisible(true);
              }}
            >
              Phản hồi
            </Button>
          </div>
        )}
      </div>
    );
  };

  const handleSubmitRegisterResult = async () => {
    const { feedback } = getValues();
    if (!feedback || !feedback?.trim()?.length) {
      toast(TOAST.ERROR, 'Vui lòng nhập thông tin phản hồi');
      return;
    }
    await sendFeedback({
      registerResultId,
      reviewId,
      body: { feedback },
    });
    toast(TOAST.SUCCESS, 'Gửi phản hồi thành công');
    refetch();
    setIsFeedbackModalVisible(false);
  };

  const footerConfirmationSubmit = () => (
    <div>
      <Button
        label={t('projectProposal.ok')}
        className="w-7rem"
        severity="info"
        onClick={handleSubmitRegisterResult}
      />
    </div>
  );

  return (
    <div className="grid">
      {/* {(isSummaryReportRevisesLoading || isSummaryReportLoading) && <Loading />} */}

      <div className="col-12">
        <div className="card">
          <div className="flex justify-content-between align-items-end mb-1 pb-2">
            <h5 className="p-0 m-0">
              {t('projectProposal.requestExplanation.updateInfoRequests')}
            </h5>
            <Button
              icon="pi pi-history"
              tooltip="Lịch sử trạng thái"
              tooltipOptions={{ position: 'left', at: 'left top' }}
              onClick={() => setVisible(true)}
            />
            <Tooltip target=".pi-history" />
          </div>
          <div className="mb-2">
            <span className="p-0 m-0 text-blue-500 font-bold text-lg">
              {t('projectProposal.label')}:{' '}
            </span>
            <span className="text-blue-500 font-bold text-lg">
              {projectData?.projectProposal?.vietnameseName}
            </span>
          </div>
          <div className="my-4 border-double p-2">
            <p className="my-1">
              <b>{t('project.explanationGuideline.title')}:</b>
            </p>
            <p>
              <b className="underline">{t('guideline.step')} 1:</b>{' '}
              {t('project.explanationGuideline.step1')}
            </p>
            <p>
              <b className="underline">{t('guideline.step')} 2:</b>{' '}
              {t('project.explanationGuideline.step2.1')}{' '}
              <b className="text-orange-400">{t('project.explanationGuideline.step2.2-1')}</b>{' '}
              {t('project.explanationGuideline.step2.3')}{' '}
              <b>&quot;{t('project.explanationGuideline.step2.6')}&quot;</b>{' '}
              {t('project.explanationGuideline.step2.5')}
            </p>
          </div>

          {data.length > 0 && (
            <Accordion
              multiple
              className="explanation-request-list"
              activeIndex={[...Array(data.length).keys()]}
            >
              {data.map((rowData, rowIndex) => (
                <AccordionTab key={rowData.id} header={formatHeader(rowData, rowIndex)}>
                  {/* Ghi chú */}
                  {formatComment(rowData)}

                  {formatFeedback(rowData)}

                  {formatActions(rowData)}
                </AccordionTab>
              ))}
            </Accordion>
          )}
        </div>
      </div>
      <RegisterResultReviewHistory
        data={registerResultReviewHistoriesUsed || []}
        visible={visible}
        setVisible={setVisible}
        title={t('registerResult.reviewHistory.title')}
      />
      <Dialog
        header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
        visible={isFeedbackModalVisible}
        position="center"
        style={{ width: '400px' }}
        onHide={() => setIsFeedbackModalVisible(false)}
        draggable={false}
        footer={footerConfirmationSubmit}
      >
        <div className="p-fluid">
          <TextareaInput
            control={control}
            errors={errors}
            name="feedback"
            row={4}
            label="Thông tin phản hồi"
            isRequired
          />
        </div>
      </Dialog>
    </div>
  );
}
